import { formatPercent } from '@kwenta/sdk/utils'
import RightArrowIcon from 'assets/svg/app/caret-right.svg'
import CrownIcon from 'assets/svg/app/crown.svg'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivRowCentered } from 'components/layout/flex'
import ROUTES from 'constants/routes'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { type FC, memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MAX_REFERRAL_SCORE, REFFERAL_TIERS } from 'sections/referrals/constants'
import { useFetchReferralData } from 'state/futures/hooks'
import { useAppSelector } from 'state/hooks'
import { selectReferralNft, selectReferralScore } from 'state/referrals/selectors'
import { selectVipTier } from 'state/vip/selectors'
import type { VipTier } from 'state/vip/types'
import styled from 'styled-components'
import BronzeIcon from '../../../../../../../public/images/png/referrals/bronze.png'
import GoldIcon from '../../../../../../../public/images/png/referrals/gold.png'
import SilverIcon from '../../../../../../../public/images/png/referrals/silver.png'

interface VipAndReferralStatusProps {
	onDismiss: () => void
}

const VipAndReferralStatus: FC<VipAndReferralStatusProps> = memo(({ onDismiss }) => {
	const { t } = useTranslation()
	const router = useRouter()
	const referralNftTier = useAppSelector(selectReferralNft)
	const vipTier = (useAppSelector(selectVipTier) as VipTier) ?? 0
	const score = useAppSelector(selectReferralScore)
	const ratio = useMemo(() => score / MAX_REFERRAL_SCORE, [score])
	const handleGoToVip = useCallback(() => {
		router.push(ROUTES.Dashboard.Vip)
		onDismiss()
	}, [onDismiss, router])

	useFetchReferralData()

	return (
		<FlexDivCol rowGap="4px">
			<FlexDivRowCentered style={{ marginTop: '36px', cursor: 'pointer' }} onClick={handleGoToVip}>
				<FlexDivRowCentered columnGap="5px">
					<CrownIcon />
					<Body color="primary" weight="bold" size="large">
						{t('dashboard.tabs.vip')}
					</Body>
				</FlexDivRowCentered>
				<Body weight="semiBold" size="medium">
					{`${t('referrals.affiliates.modal.nft-preview.tier-level')} ${vipTier + 1}`}
				</Body>
				<StyledRightArrowIcon width={8} height={8} />
			</FlexDivRowCentered>
			<ProgressBar>
				<LineCotainer>
					<FilledLine $ratio={formatPercent(ratio)} />
				</LineCotainer>
				<DotsContainer justifyContent="space-between">
					<StyledImage
						src={BronzeIcon}
						width={20}
						height={20}
						alt="bronze-icon"
						$active={REFFERAL_TIERS[referralNftTier].tier === 0}
					/>
					<StyledImage
						src={SilverIcon}
						width={20}
						height={20}
						alt="silver-icon"
						$active={REFFERAL_TIERS[referralNftTier].tier === 1}
					/>
					<StyledImage
						src={GoldIcon}
						width={20}
						height={20}
						alt="gold-icon"
						$active={REFFERAL_TIERS[referralNftTier].tier === 2}
					/>
				</DotsContainer>
			</ProgressBar>
		</FlexDivCol>
	)
})

const StyledRightArrowIcon = styled(RightArrowIcon)`
	fill: ${(props) => props.theme.colors.selectedTheme.newTheme.text.secondary};
`

const DotsContainer = styled(FlexDivRowCentered)`
	width: 100%;
`

const StyledImage = styled(Image)<{ $active: boolean }>`
	z-index: 0;
	border-radius: 4px;
	border: 1px solid
		${(props) =>
			props.$active
				? props.theme.colors.selectedTheme.newTheme.button.fund.activeBorder
				: props.theme.colors.selectedTheme.newTheme.border.color};
`

const LineCotainer = styled.div`
	position: absolute;
	top: 50%;
	left: 0;
	width: 100%;
	height: 4px;
	background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.progressBar.background};
	transform: translateY(-50%);
`

const FilledLine = styled.div<{ $ratio: string }>`
	width: ${(props) => props.$ratio};
	height: 100%;
	background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.checkBox.default.checked};
	border-radius: 100px;
`

const ProgressBar = styled.div`
	position: relative;
	width: 170px;
	border-radius: 100px;
`

export default VipAndReferralStatus
